import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";

const faqItems = [
  {
    question: "Skąd pochodzą dane o schroniskach?",
    answer: (
      <>
        Część danych <strong>inicjalnie zebraliśmy z publicznych źródeł internetowych</strong> - stron schronisk, portali adopcyjnych i mediów społecznościowych. Jednak <strong>użytkownicy, wolontariusze i same schroniska</strong> mogą również dodawać nowe organizacje oraz uzupełniać dane przez formularz zgłoszeniowy. Dodatkowo <strong>sami kontaktujemy się ze schroniskami</strong>, zachęcając je do weryfikacji swoich profili. Gdy schronisko potwierdzi swoje dane, otrzymuje status <strong>«Zweryfikowane przez organizację»</strong>. W przypadku schronisk bez tego statusu zalecamy <strong>potwierdzenie informacji bezpośrednio z organizacją</strong> przed wizytą.
      </>
    ),
  },
  {
    question: "Jak działają pilne potrzeby?",
    answer: (
      <>
        Gdy ktoś dowie się od schroniska o pilnej potrzebie, może oznaczyć ją jako <strong>"PILNE"</strong> przy zgłaszaniu aktualizacji. Badge "PILNE" pozostaje widoczny dopóki ktoś go nie zmieni lub <strong>automatycznie wygasa po miesiącu</strong> - dzięki temu unikamy sytuacji, gdy nieaktualna pilna potrzeba jest wyświetlana przez długi czas.
      </>
    ),
  },
  {
    question: "Jak korzystać z filtrów wyszukiwania?",
    answer: (
      <>
        Na stronie głównej znajdziesz pasek filtrów, który pomoże Ci szybko znaleźć odpowiednie schronisko. Możesz filtrować według: <strong>Zweryfikowane</strong> (schroniska z potwierdzonym profilem), <strong>Pilne potrzeby</strong> (miejsca potrzebujące pilnej pomocy), <strong>Wolontariat</strong> (schroniska przyjmujące wolontariuszy), <strong>Spacery z psami</strong> (możliwość wyprowadzania psów), oraz <strong>Psy/Koty</strong> (dostępność zwierząt do adopcji). Dodatkowo możesz wyszukiwać po nazwie schroniska lub filtrować według miasta.
      </>
    ),
  },
  {
    question: "Jak działa funkcja «Udostępnij potrzeby»?",
    answer: (
      <>
        Funkcja pozwala wygenerować grafikę z potrzebami schroniska, którą możesz udostępnić w mediach społecznościowych. Do wyboru masz trzy formaty: <strong>Post (4:5)</strong> idealny na Instagram i Facebook, <strong>Story (9:16)</strong> do relacji na Instagramie, oraz <strong>Kompaktowy</strong> z mniejszą ilością miejsca na tekst. W oknie generatora możesz edytować tekst potrzeb, oznaczyć je jako "PILNE" oraz wybrać <strong>do 3 schronisk</strong> na jednej grafice. Ważne: wszystkie zmiany wprowadzone w generatorze służą wyłącznie do stworzenia grafiki i <strong>nie nadpisują danych w bazie</strong> serwisu.
      </>
    ),
  },
  {
    question: "Jak działa funkcja «Udostępnij profil»?",
    answer: (
      <>
        Funkcja pozwala wygenerować grafikę z profilem schroniska, którą możesz udostępnić w mediach społecznościowych. Do wyboru masz dwa formaty: <strong>Post (4:5)</strong> najlepiej pasujący do postów na Instagramie i Facebooku, oraz <strong>Story (9:16)</strong> najlepiej pasujący do relacji na Instagramie i Facebooku. W oknie generatora możesz <strong>edytować opis schroniska</strong> oraz <strong>informacje o wolontariacie</strong> przed wygenerowaniem grafiki. Ważne: wszystkie zmiany wprowadzone w generatorze służą wyłącznie do stworzenia grafiki i <strong>nie nadpisują danych w bazie</strong> serwisu.
      </>
    ),
  },
  {
    question: "Kto może aktualizować dane o schroniskach?",
    answer: (
      <>
        <strong>Każdy!</strong> Nie musisz być pracownikiem schroniska - wystarczy, że masz aktualne informacje. Jeśli jednak reprezentujesz organizację i zaznaczysz to w formularzu, po weryfikacji schronisko otrzyma specjalny status <strong>"Zweryfikowane przez organizację"</strong>. Chcesz regularnie aktualizować dane konkretnego schroniska? Możesz zostać jego <strong>administratorem</strong> - napisz do nas!
      </>
    ),
  },
  {
    question: "Jak działa proces aktualizacji?",
    answer: (
      <>
        Kliknij <strong>"Zgłoś aktualizację"</strong> na stronie schroniska i wypełnij formularz. Twoje zgłoszenie trafi do panelu administracyjnego, gdzie zostanie sprawdzone i zatwierdzone lub odrzucone. Weryfikacja zajmuje <strong>do 24 godzin</strong>, ale zazwyczaj jest szybsza. Chcesz aktualizować dane na bieżąco? Zostań <strong>administratorem schroniska</strong> - wtedy zmiany będą widoczne od razu!
      </>
    ),
  },
  {
    question: "Czy mogę dodać nowe schronisko do bazy?",
    answer: (
      <>
        <strong>Tak!</strong> Jeśli znasz schronisko, którego nie ma w naszej bazie, możesz je zgłosić przez formularz. Wystarczy podać <strong>podstawowe dane</strong> - nazwę, miasto i lokalizację. Po weryfikacji schronisko zostanie dodane do mapy.
      </>
    ),
  },
  {
    question: "Co oznacza «Dane niezweryfikowane»?",
    answer: (
      <>
        Oznaczenie <strong>«Dane niezweryfikowane»</strong> pojawia się przy schroniskach, których informacje zostały pobrane automatycznie z internetu i nikt jeszcze ich nie zaktualizował przez formularz. Jeśli schronisko <strong>nie ma żadnego oznaczenia</strong> - oznacza to, że ktoś (np. wolontariusz lub użytkownik) zgłosił aktualizację danych, ale nie udało nam się jeszcze potwierdzić tego z samą organizacją. Natomiast status <strong>«Zweryfikowane przez organizację»</strong> oznacza, że przedstawiciel schroniska osobiście potwierdził dane. Zalecamy <strong>kontakt bezpośrednio ze schroniskiem</strong> w celu potwierdzenia aktualności informacji.
      </>
    ),
  },
  {
    question: "Co oznacza badge «Zweryfikowane przez organizację»?",
    answer: (
      <>
        To oznaczenie informuje, że dane schroniska lub fundacji zostały <strong>potwierdzone bezpośrednio przez samą organizację</strong>. Jak to działa? Przedstawiciel organizacji zgłasza aktualizację danych i potwierdza, że ją reprezentuje. Następnie administrator serwisu sprawdza zgłoszenie (np. adres e-mail lub zgodność informacji). Dopiero po pozytywnej weryfikacji przyznawany jest status <strong>"Zweryfikowane"</strong>.
      </>
    ),
  },
  {
    question: "Czy mogę adoptować zwierzę przez tę stronę?",
    answer: (
      <>
        <strong>Nie</strong>, nasza strona jest katalogiem schronisk i nie pośredniczymy w adopcjach. Aby adoptować zwierzę, <strong>skontaktuj się bezpośrednio ze schroniskiem</strong> - dane kontaktowe znajdziesz na karcie każdego schroniska. Przed wizytą warto zadzwonić do schroniska i potwierdzić aktualne godziny otwarcia oraz dostępność zwierząt - szczególnie jeśli profil nie ma statusu <strong>«Zweryfikowane przez organizację»</strong>.
      </>
    ),
  },
  {
    question: "Czym jest strona «Dla schronisk»?",
    answer: (
      <>
        To specjalna podstrona z <strong>broszurą informacyjną</strong> przeznaczoną dla pracowników i wolontariuszy schronisk. Znajdziesz tam <strong>gotową do pobrania grafikę</strong> wyjaśniającą, jak działa nasz serwis i jak schronisko może z niego korzystać. Broszurę możesz wydrukować lub udostępnić w mediach społecznościowych, aby zachęcić innych do pomocy.
      </>
    ),
  },
];

interface FAQModalProps {
  children: React.ReactNode;
}

export const FAQModal = ({ children }: FAQModalProps) => {
  return (
    <Dialog onOpenChange={(open) => { if (open) trackEvent("FAQ - otwarto"); }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-primary" />
            </div>
            <span className="font-display">Najczęściej zadawane pytania</span>
          </DialogTitle>
        </DialogHeader>

        <Accordion type="single" collapsible className="w-full mt-4">
          {faqItems.map((item, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left text-sm hover:no-underline">{item.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </DialogContent>
    </Dialog>
  );
};
